import { FileItemProps } from '@components/Core/Folders/FileItem/FileItem.component';
import { resolve } from 'path';
import BaseServices from './BaseServices';
import { FileTransaction, updateFileWithTransaction } from './FolderHelper';
import ServicesConstants from './Global';
import {
  AiUserCountDTO,
  DashboardView,
  FunctionalAreaDto,
  ICDealspace,
  ICFreeText,
  ISUpdateMergefloDealspaceUser,
  ISUpdateMergefloDependency,
  ISUpdateMergefloPlaybook,
  ISUpdateMergefloTask,
  PaymentDto,
} from './Interfaces';

export class MergefloServices {
  services: BaseServices;
  constructor() {
    this.services = new BaseServices();
  }

  async createIssue(
    name: string,
    parentId: string,
    referenceTaskId?: string,
    isBefore?: boolean
  ) {
    let params = { name: name, parentId: parentId };
    if (referenceTaskId) {
      (params as any).referenceTaskId = referenceTaskId;
      (params as any).isBefore = isBefore ?? false;
    }
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.issue.create,
        params,
        config
      )
      .then(response => response);
  }

  async createRisk(
    name: string,
    parentId: string,
    referenceTaskId?: string,
    isBefore?: boolean
  ) {
    let params = { name: name, parentId: parentId };
    if (referenceTaskId) {
      (params as any).referenceTaskId = referenceTaskId;
      (params as any).isBefore = isBefore ?? false;
    }
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.risk.create,
        params,
        config
      )
      .then(response => response);
  }

  async changeTemplate(playbookId: string, revert: boolean) {
    let params = {};
    let config = {};
    return this.services
      .post(
        revert
          ? ServicesConstants.ServicesConstants.mergeflo.playbook.revertTemplate.replace(
              ServicesConstants.idTag,
              playbookId
            )
          : ServicesConstants.ServicesConstants.mergeflo.playbook.makeItTemplate.replace(
              ServicesConstants.idTag,
              playbookId
            ),
        params,
        config
      )
      .then(response => response);
  }

  async getPersonalTemplates() {
    const personalTemplates =
      (await this.services.get(
        ServicesConstants.ServicesConstants.mergeflo.playbook.personalTemplates,
        {},
        {}
      )) || [];

    return new Promise<FileItemProps[]>((resolve, reject) => {
      this.services
        .get(
          ServicesConstants.ServicesConstants.mergeflo.playbook
            .templateStructure,
          {},
          {}
        )
        .then((template: any) => {
          const files =
            (template['personal_templates'] as FileItemProps[]) || [];
          let sourcePath = { name: 'root', files } as FileItemProps;
          personalTemplates.forEach(
            ({
              contains,
              copied,
              copiedTo,
              templateId = '',
            }: FileItemProps) => {
              sourcePath = updateFileWithTransaction({
                file: { id: templateId } as FileItemProps,
                newFile: { contains, copied, copiedTo } as FileItemProps,
                sourcePath,
              } as FileTransaction);
            }
          );
          resolve(sourcePath.files);
        });
    });
  }

  async getTemplateStructure() {
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.playbook.templateStructure,
        {},
        {}
      )
      .then(response => response);
  }

  async setTemplateStructure(structure: any) {
    const params = { structure };
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.playbook.templateStructure,
        params,
        {}
      )
      .then(response => response);
  }

  async createNewTemplateStructure() {
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.playbook.createNewTemplate,
        {},
        {}
      )
      .then(response => response);
  }

  async reminderEmailForProjectUser(projectUserId: string) {
    let config = {};
    let params = { mailResend: true };
    return this.services
      .put(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceUser.reminder.replace(
          ServicesConstants.idTag,
          projectUserId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createDecision(
    name: string,
    parentId: string,
    referenceTaskId?: string,
    isBefore?: boolean
  ) {
    let params = { name: name, parentId: parentId };
    if (referenceTaskId) {
      (params as any).referenceTaskId = referenceTaskId;
      (params as any).isBefore = isBefore ?? false;
    }
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.decision.create,
        params,
        config
      )
      .then(response => response);
  }

  async createDependency(
    name: string,
    parentId: string,
    functionalAreaId: string
  ) {
    let params = {
      name: name,
      parentId: parentId,
      functionalAreaId: functionalAreaId,
    };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dependency.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveDependency(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dependency.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateDependency(id: string, newValues: ISUpdateMergefloDependency) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.dependency.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async createTask(
    name: string,
    dealspaceId: string,
    playbookId: string,
    sectionId: string
  ) {
    let params = {
      playbookId: playbookId,
      name: name,
      dealspaceId: dealspaceId,
      sectionId: sectionId,
    };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.task.create,
        params,
        config
      )
      .then(response => response);
  }

  async createSubTask(
    name: string,
    parentId: string,
    referenceTaskId?: string,
    isBefore?: boolean
  ) {
    let params = { name: name, parentId: parentId };
    if (referenceTaskId) {
      (params as any).referenceTaskId = referenceTaskId;
      (params as any).isBefore = isBefore;
    }
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.task.createSubTask,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveTask(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.task.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateTask(id: string, newValues: ISUpdateMergefloTask) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.task.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteDealspaceUser(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceUser.removeDealspaceUser.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async deleteTask(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.task.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createDealspace(name: string) {
    let params = { name: name };
    let config = {};

    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveDealspace(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listDealspaceUsers(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.users.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getDealspaceTasks(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.tasks.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getDealspacePrefixes(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.taskPrefixes.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getDirectory(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceDirectory.getDirectory.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getWorkspaceUserList(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.workspace.list.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async aiUserCount(dealspaceId: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceSubscribe
          .aiUserCount,
        { dealspaceId: dealspaceId },
        config
      )
      .then(response => response);
  }

  async aiFeaturePrice(dealspaceId: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceSubscribe
          .aiFeaturePrice,
        { dealspaceId: dealspaceId },
        config
      )
      .then(response => response);
  }

  async createDealspaceSubscribe(priceDto: PaymentDto) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceSubscribe.create,
        priceDto,
        config
      )
      .then(response => response);
  }

  async downgradeDealspace(dealspaceId: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.downgrade.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        {},
        config
      )
      .then(response => response);
  }

  async updateDealspace(id: string, newValues: ICDealspace) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async changeDealspaceUser(
    id: string,
    newValues: ISUpdateMergefloDealspaceUser
  ) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceUser.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteDealspace(id: string) {
    let params = {};
    let config = {};
    return this.services.delete(
      ServicesConstants.ServicesConstants.mergeflo.dealspace.delete.replace(
        ServicesConstants.idTag,
        id
      ),
      params,
      config
    );
  }

  async createDealspaceUser(
    dealspaceId: string,
    username: string[],
    functionalAreaId: string,
    invitationMessage: string
  ) {
    let params = {
      //dealspaceId: dealspaceId,
      username: username,
      projectId: functionalAreaId,
      inviteMessage: invitationMessage,
    };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.core.projectUser.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveDealspaceUser(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceUser.retrieve.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }
  async acceptDealspaceUser(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceUser.accept.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async rejectDealspaceUser(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceUser.reject.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createFunctionalArea(functionalAreaDto: FunctionalAreaDto) {
    let params = functionalAreaDto;
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.functionalArea.create,
        params,
        config
      )
      .then(response => response);
  }

  async listFunctionalAreas() {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.functionalArea.list,
        params,
        config
      )
      .then(response => response);
  }

  async listFunctionalAreasByDealspaceId(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.functionalArea.listFunctionalAreasByDealspaceId.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async retrieveFunctionalArea(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.functionalArea.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateFunctionalArea(id: string, functionalAreaDto: FunctionalAreaDto) {
    let params = functionalAreaDto;
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.functionalArea.update.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async deleteFunctionalArea(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.functionalArea.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createPlaybook(
    name: string,
    functionalAreaId: string,
    taskPrefix: string,
    dealspaceId: string
  ) {
    let params = {
      name: name,
      dealspaceId: dealspaceId,
      taskPrefix: taskPrefix,
      //functionalAreaId: functionalAreaId,
      functionalArea: {
        name: name,
        shortName: taskPrefix,
        color: '#1a7d77',
        id: functionalAreaId,
      },
    };

    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.playbook.create,
        params,
        config
      )
      .then(response => response);
  }

  async createPlaybookEmpty(
    name: string,
    functionalAreaId: string,
    taskPrefix: string,
    dealspaceId: string,
    functionalAreaTypeId: string
  ) {
    let params = {
      name: name,
      dealspaceId: dealspaceId,
      taskPrefix: taskPrefix,
      //functionalAreaId: functionalAreaId,
      functionalArea: {
        name: name,
        shortName: taskPrefix,
        color: '#1a7d77',
        id: functionalAreaId,
        functionalAreaTypeId: functionalAreaTypeId,
      },
    };

    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.playbook.create,
        params,
        config
      )
      .then(response => response);
  }
  async removeFromSubscription(playbookId: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.playbook.removeFromSubscription.replace(
          ServicesConstants.idTag,
          playbookId
        ),
        {},
        config
      )
      .then(response => response);
  }

  async addToSubscription(playbookId: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.playbook.addToSubscription.replace(
          ServicesConstants.idTag,
          playbookId
        ),
        {},
        config
      )
      .then(response => response);
  }

  async retrievePlaybook(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.playbook.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updatePlaybook(id: string, newValues: ISUpdateMergefloPlaybook) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.playbook.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deletePlaybook(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.playbook.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listPlaybookTasks(id: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.playbook.listTasks.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async buildFromTemplate(playbookId: string) {
    let params = {};
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.playbook.buildFromTemplate.replace(
          ServicesConstants.idTag,
          playbookId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async importPlaybookTemplate(files: any, playbookId: string) {
    return this.services
      .postFormData(
        ServicesConstants.ServicesConstants.mergeflo.playbook.importTemplate.replace(
          ServicesConstants.idTag,
          playbookId
        ),
        files
      )
      .then(response => response);
  }

  async importCSVPersonalTemplate(files: any) {
    console.log({ files });

    return this.services
      .postFormData(
        ServicesConstants.ServicesConstants.mergeflo.playbook
          .importFromCSVPersonalTemplates,
        files
      )
      .then(response => response);
  }

  async makeItPersonalTemplate(params: any, playbookId: string) {
    const fileData = await this.services.post(
      ServicesConstants.ServicesConstants.mergeflo.playbook.makeItPersonalTemplate.replace(
        ServicesConstants.idTag,
        playbookId
      ),
      params,
      {}
    );
    let { personal_templates = [] } = await this.getTemplateStructure();
    personal_templates = [...personal_templates, fileData as FileItemProps];
    return this.setTemplateStructure({ personal_templates });
  }

  async swimlaneOrder(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.swimlaneOrder.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async playbookSummary(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.playbookSummary.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getDealspaceFreeText(id: string, type: 'playbook' | 'default') {
    let params = {};
    let config = {};
    let url =
      type === 'playbook'
        ? ServicesConstants.ServicesConstants.mergeflo.freetext.playbookList
        : ServicesConstants.ServicesConstants.mergeflo.freetext.list;
    return this.services
      .get(url.replace(ServicesConstants.idTag, id), params, config)
      .then(response => response);
  }

  async updateSwimlaneOrder(dealspaceId: string, order: any) {
    let params = {
      order,
    };

    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.swimlaneOrder.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createDealspaceFreeText(
    id: string,
    title: string,
    data: string,
    type: 'playbook' | 'default'
  ) {
    let params = {
      title: title,
      data: data,
    };
    let config = {};
    let url =
      type === 'playbook'
        ? ServicesConstants.ServicesConstants.mergeflo.freetext.playbookCreate
        : ServicesConstants.ServicesConstants.mergeflo.freetext.create;

    return this.services
      .post(url.replace(ServicesConstants.idTag, id), params, config)
      .then(response => response);
  }

  async updateDealspaceFreeText(id: string, freetext: ICFreeText) {
    let params = {
      text: freetext.text,
      title: freetext.title,
    };
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.freetext.update.replace(
          ServicesConstants.idTag,
          freetext.id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async deleteDealspaceFreeText(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.freetext.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getDealBackground(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealBackground.list.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createDealBackground(id: string, data: string) {
    let params = {
      data: data,
    };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.dealBackground.create.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateDealBackground(id: string, freetext: ICFreeText) {
    let params = {
      text: freetext.text,
    };
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.dealBackground.update.replace(
          ServicesConstants.idTag,
          freetext.id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async deleteDealBackground(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.dealBackground.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getAccomplishments(id: string, type: 'playbook' | 'default') {
    let params = {};
    let config = {};
    let url =
      type === 'playbook'
        ? ServicesConstants.ServicesConstants.mergeflo.accomplishments
            .playbookList
        : ServicesConstants.ServicesConstants.mergeflo.accomplishments.list;

    return this.services
      .get(url.replace(ServicesConstants.idTag, id), params, config)
      .then(response => response);
  }

  async listPlaybooks(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.listPlaybooks.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async bootTemplates(id: string, templateCollection: string[]) {
    let params = {
      templateIds: templateCollection,
    };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.dealspace.bootTemplates.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async importFromTemplates(id: string, templateCollection: string[]) {
    let params = {
      templateIds: templateCollection,
    };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.playbook.importFromTemplates.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async importFromPersonalTemplates(id: string, templateCollection: string[]) {
    let params = {
      templateIds: templateCollection,
    };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.playbook.importFromPersonalTemplates.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listTemplateCollection() {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.template.getTemplates,
        params,
        config
      )
      .then(response => response);
  }

  async portalLink(link: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.payment.portalLink,
        { returnUrl: link },
        config
      )
      .then(response => response);
  }

  async dealspaceSubscribeCancel(dealspaceId: string) {
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.payment
          .dealspaceSubscribeCancel,
        { dealspaceId: dealspaceId },
        config
      )
      .then(response => response);
  }

  async workstreamPrice(dealspaceId: string) {
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceSubscribe
          .basePackagePrice,
        { dealspaceId: dealspaceId },
        config
      )
      .then(response => response);
  }

  async getDealspaceSubscribe(dealspaceId: string) {
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceSubscribe.getDealspaceSubscribe.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        {},
        config
      )
      .then(response => response);
  }

  async getDealspaceSubscribe2(dealspaceId: string) {
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.dealspaceSubscribe.getDealspaceSubscribe.replace(
          ServicesConstants.idTag,
          dealspaceId
        ),
        {},
        config
      )
      .then(response => response);
  }

  async createAccomplishments(
    id: string,
    data: string,
    date: string,
    type: 'playbook' | 'default'
  ) {
    let params = {
      data: data,
      date: date,
    };
    let config = {};
    let url =
      type === 'playbook'
        ? ServicesConstants.ServicesConstants.mergeflo.accomplishments
            .playbookCreate
        : ServicesConstants.ServicesConstants.mergeflo.accomplishments.create;
    return this.services
      .post(url.replace(ServicesConstants.idTag, id), params, config)
      .then(response => response);
  }

  async updateAccomplishments(id: string, freetext: ICFreeText) {
    let params = {
      text: freetext.text,
      date: freetext.date,
    };
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.accomplishments.update.replace(
          ServicesConstants.idTag,
          freetext.id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async deleteAccomplishments(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.mergeflo.accomplishments.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getDashboardWidget(
    id: string,
    type: 'Imo' | 'Workstream' | 'Executive'
  ) {
    let params = {};
    let config = {};
    let url = '';
    if (type === 'Imo') {
      url = ServicesConstants.ServicesConstants.mergeflo.dashboardImo.list;
    } else if (type === 'Executive') {
      url =
        ServicesConstants.ServicesConstants.mergeflo.dashboardExecutive.list;
    } else if (type === 'Workstream') {
      url =
        ServicesConstants.ServicesConstants.mergeflo.dashboardWorkstream.list;
    }
    return this.services
      .get(url.replace(ServicesConstants.idTag, id), params, config)
      .then(response => {
        if (response?.widgets) {
          return response?.widgets;
        } else {
          return [];
        }
      });
  }
}
